import Stack from '@mui/material/Stack'
import { Typography } from '@mui/material/'
import React, { ComponentProps, useCallback, useMemo, useState } from 'react'
import Button from '@mui/material/Button'
import { ReactComponent as LinkIcon } from '../../assets/images/hyperlink.svg'
import Tabs from '../../shared/components/Tabs/Tabs'
import useValueMessageDisplayName from '../../shared/hooks/useDisplayNames/variants/useValueMessageDisplayName'
import useActivitiesDisplayName from '../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import useEvidenceDisplayName from '../../shared/hooks/useDisplayNames/variants/useEvidenceDisplayName'

import Tooltip from '@mui/material/Tooltip'

import { EmptyPlaceholder } from '../../shared/components/EmptyPlaceholder'
import { useBooleanState } from '../../shared/hooks/useBooleanState'
import LinkedItemsList from './components/LinkedItems/LinkedItemsList'
import { useIsRoadmapReadonly } from '../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../shared/hooks/useIsSnapshotMode'
import useStrategicImperativeDisplayName from '../../shared/hooks/useDisplayNames/variants/useStrategicImperativeDisplayName'
import { LinkingItem, LinkingItemsIds, LinkingItemType } from './Linking.types'
import { useGetLinkedItemsQuery } from '../../shared/queries/linking/useGetLinkedItemsQuery'
import { useUpdateLinkedItemsMutation } from '../../shared/queries/linking/useUpdateLinkedItemsMutation'
import LinkableItemsModal from './components/LinkableItems/LinkableItemsModal'
import LoadingBackdrop from '../../shared/components/LoadingBackdrop/LoadingBackdrop'
import { Fade } from '@mui/material'
import { useItemGrouping } from '../../shared/hooks/linking/useItemGrouping'

export type StrategicImperative = {
    _id: string
    name: string
}

export type LinkingProps = {
    id: LinkingItem['_id']
    type: LinkingItem['type']
    previousDataStack: unknown[]
    disabled?: boolean
    // Required when type is 'activity'. See useGetLinkedItemsQuery/useUpdateLinkedItemsMutation
    // for why: an Activity can be shared across roadmaps, so reads and writes both need to know
    // which roadmap is asking. Ignored for every other type.
    roadmapId?: string
}

const Linking = React.memo(
    ({ id, type, previousDataStack, disabled = false, roadmapId }: LinkingProps) => {
        const getLinkedItemsQuery = useGetLinkedItemsQuery(id, type, roadmapId)

        const linkingModal = useBooleanState()

        const [selectedTabId, setSelectedTabId] = useState<LinkingItem['type']>(null)

        const updateLinkedItemsMutation = useUpdateLinkedItemsMutation({
            onSuccess: () => {
                linkingModal.handleSetAsFalse()
            },
            onError: (error) => {
                console.error('Failed to update linked items:', error)
            },
        })

        const valueMessageDisplayName = useValueMessageDisplayName()
        const primaryValueMessageDisplayName = {
            singular: 'Primary ' + valueMessageDisplayName.singular,
            plural: 'Primary ' + valueMessageDisplayName.plural,
        }
        const activityDisplayName = useActivitiesDisplayName()
        const evidenceDisplayName = useEvidenceDisplayName()
        const primaryEvidenceDisplayName = {
            singular: 'Primary ' + evidenceDisplayName.singular,
            plural: 'Primary ' + evidenceDisplayName.plural,
        }
        const strategicImperativeDisplayName = useStrategicImperativeDisplayName()

        const isReadonlyMode = useIsRoadmapReadonly()
        const isSnapshotMode = useIsSnapshotMode()

        const evidenceItemsWithKeyEvidenceStatements = useMemo(() => {
            if (!getLinkedItemsQuery.data?.evidence) return []

            const evidenceItemsMap = getLinkedItemsQuery.data.evidence.reduce(
                (acc, evidenceItem) => {
                    return {
                        ...acc,
                        [evidenceItem._id]: evidenceItem,
                    }
                },
                {} as Record<LinkingItem['_id'], LinkingItem>
            )

            getLinkedItemsQuery.data.keyEvidenceStatement?.forEach((keyEvidenceStatementItem) => {
                if (!keyEvidenceStatementItem.extras.parent?._id) return
                const parentEvidence = { ...evidenceItemsMap[keyEvidenceStatementItem.extras.parent._id] }

                if (!parentEvidence.children) parentEvidence.children = []

                parentEvidence.children.push(keyEvidenceStatementItem)

                evidenceItemsMap[parentEvidence._id] = parentEvidence
            })

            return Object.values(evidenceItemsMap)
        }, [getLinkedItemsQuery.data])

        const linkedItems = useMemo(() => {
            const linkedItems = { ...getLinkedItemsQuery.data }

            if (linkedItems?.evidence) {
                linkedItems.evidence = evidenceItemsWithKeyEvidenceStatements
            }

            return linkedItems
        }, [evidenceItemsWithKeyEvidenceStatements])

        const valueMessageGroups = useItemGrouping(linkedItems.valueMessage, 'valueMessage')
        const activityGroups = useItemGrouping(linkedItems.activity, 'activity')

        const getTabLabelByLinkingType = useCallback(
            (linkingType: LinkingItemType) => {
                const numberOfLinks = linkedItems?.[linkingType]?.length || 0

                const formatTabLabel = (displayName: string) => {
                    return `${displayName} (${numberOfLinks})`
                }

                switch (linkingType) {
                    case 'valueMessage':
                        return formatTabLabel(valueMessageDisplayName.plural)
                    case 'activity':
                        return formatTabLabel(activityDisplayName.plural)
                    case 'evidence':
                        return formatTabLabel(evidenceDisplayName.plural)
                    case 'primaryEvidence':
                        return formatTabLabel(primaryEvidenceDisplayName.plural)
                    case 'primaryValueMessage':
                        return formatTabLabel(primaryValueMessageDisplayName.plural)
                    case 'strategicImperative':
                        return formatTabLabel(strategicImperativeDisplayName.plural)
                    default:
                        return null
                }
            },
            [linkedItems]
        )

        const getCommonItemLinkingProps = (pathname: string, linkingItem: Partial<LinkingItem>) => {
            return {
                pathname,
                autoOpenData: {
                    isAutoOpenPanel: true,
                    autoOpenData: {
                        _id: linkingItem._id,
                        ...(linkingItem.extras || {}),
                    },
                    previousDataStack,
                },
            }
        }

        const getTabViewByLinkingType = useCallback(
            (linkingType: LinkingItemType) => {
                const getView = () => {
                    switch (linkingType) {
                        case 'valueMessage':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={valueMessageDisplayName}
                                    items={linkedItems.valueMessage}
                                    groups={valueMessageGroups.groups}
                                    getItemLinkingProps={(valueMessageItem) => {
                                        if (
                                            !valueMessageItem?.extras?.categoryId ||
                                            !valueMessageItem?.extras?.narrativeId
                                        )
                                            return null

                                        return getCommonItemLinkingProps('/narratives', valueMessageItem)
                                    }}
                                />
                            )
                        case 'primaryValueMessage':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={primaryValueMessageDisplayName}
                                    items={linkedItems.primaryValueMessage}
                                    getItemLinkingProps={(valueMessageItem) => {
                                        if (
                                            !valueMessageItem?.extras?.categoryId ||
                                            !valueMessageItem?.extras?.narrativeId
                                        )
                                            return null

                                        return getCommonItemLinkingProps('/narratives', valueMessageItem)
                                    }}
                                />
                            )
                        case 'activity':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={activityDisplayName}
                                    items={linkedItems.activity}
                                    groups={activityGroups.groups}
                                    getItemLinkingProps={(activityItem) => {
                                        return getCommonItemLinkingProps('/activities', activityItem)
                                    }}
                                />
                            )
                        case 'strategicImperative':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={strategicImperativeDisplayName}
                                    items={linkedItems.strategicImperative}
                                    getItemLinkingProps={(strategicImperativeItem) => {
                                        return getCommonItemLinkingProps(
                                            '/strategic-imperatives',
                                            strategicImperativeItem
                                        )
                                    }}
                                />
                            )
                        case 'evidence':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={evidenceDisplayName}
                                    items={linkedItems.evidence}
                                    getItemLinkingProps={(evidenceItem) => {
                                        return getCommonItemLinkingProps('/evidence', evidenceItem)
                                    }}
                                />
                            )
                        case 'primaryEvidence':
                            return (
                                <LinkedItemsList
                                    itemDisplayName={primaryEvidenceDisplayName}
                                    items={linkedItems.primaryEvidence}
                                    getItemLinkingProps={(evidenceItem) => {
                                        return getCommonItemLinkingProps('/evidence', evidenceItem)
                                    }}
                                />
                            )
                        default:
                            return null
                    }
                }

                return (
                    <Fade in key={linkingType} timeout={800}>
                        <Stack flex={1}>{getView()}</Stack>
                    </Fade>
                )
            },
            [linkedItems, valueMessageGroups.groups, activityGroups.groups]
        )

        const tabs = useMemo(
            (): ComponentProps<typeof Tabs>['tabs'] =>
                Object.keys(linkedItems || {})
                    .map((linkingType: LinkingItem['type']) => ({
                        id: linkingType,
                        label: getTabLabelByLinkingType(linkingType),
                        view: getTabViewByLinkingType(linkingType),
                    }))
                    .filter((tab) => !!tab.label),
            [getTabViewByLinkingType]
        )

        const handleSave = async (selectedIds: LinkingItemsIds) => {
            await updateLinkedItemsMutation.mutateAsync({
                id,
                type,
                selectedIds,
                roadmapId,
            })
        }

        const isEmpty = tabs.length === 0

        const isGroupLoading = valueMessageGroups.isLoading || activityGroups.isLoading

        if (getLinkedItemsQuery.isLoading || isGroupLoading) {
            return <LoadingBackdrop loading title="Linked data" subtitle="Fetching Linked Data" />
        }

        return (
            <Stack direction="column" my={2} gap={2} flex={1}>
                <Typography variant="18x600">Linked data</Typography>
                {isEmpty && (
                    <Stack
                        flex={1}
                        direction="column"
                        gap={1}
                        px={2}
                        py={2.75}
                        bgcolor="figma.grayscale.tier7"
                        borderRadius="16px"
                    >
                        <EmptyPlaceholder title="" subtitle="No data to display" />
                    </Stack>
                )}
                {!isEmpty && (
                    <>
                        {linkingModal.state && (
                            <LinkableItemsModal
                                type={type}
                                linkedItems={linkedItems}
                                handleClose={linkingModal.handleSetAsFalse}
                                handleSave={handleSave}
                                isSaving={updateLinkedItemsMutation.isLoading}
                                selectedTabId={selectedTabId}
                                onTabChange={setSelectedTabId}
                            />
                        )}
                        <Tabs
                            orientation="horizontal"
                            tabs={tabs}
                            selectedTabId={selectedTabId}
                            onTabChange={(newTabId) => setSelectedTabId(newTabId as LinkingItem['type'])}
                            tabPanelSx={{ px: 2, py: 3 }}
                            tabListSx={{
                                flexDirection: 'row',
                            }}
                            tabListWrapper={(tabList) => (
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    {tabList}
                                    {!isSnapshotMode && !isReadonlyMode && !disabled && (
                                        <Tooltip title="Link or unlink data" arrow>
                                            <Button
                                                color="primary"
                                                size="small"
                                                // @ts-expect-error Button theming needs to be improved
                                                variant="contained-link-unlink"
                                                onClick={linkingModal.handleSetAsTrue}
                                            >
                                                <LinkIcon />
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Stack>
                            )}
                        />
                    </>
                )}
            </Stack>
        )
    },
    (prevProps, nextProps) => {
        return (
            prevProps.id === nextProps.id &&
            prevProps.type === nextProps.type &&
            prevProps.roadmapId === nextProps.roadmapId
        )
    }
)

export default Linking
